import Axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { useAuthStore } from "src/store/auth.state";
import RefreshTokenService from "@services/refreshToken";

type RetryConfig = InternalAxiosRequestConfig & { _retry?: boolean };

export const onRequest = (
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig => {
  const accessToken = localStorage.getItem("access_token");
  if (accessToken) {
    config.headers["Authorization"] = `Bearer ${accessToken}`;
  }
  return config;
};

export const onRequestError = (error: AxiosError): never => {
  console.error("Request error:", error);
  throw error;
};

export const onResponse = (response: AxiosResponse): AxiosResponse => response;

export const onResponseError = async (
  error: unknown,
): Promise<AxiosResponse> => {
  console.error("Response error:", error);

  if (!Axios.isAxiosError(error)) {
    throw error instanceof Error
      ? error
      : new Error(JSON.stringify(error, null, 2) || "Unknown error");
  }
  const axiosError = error;

  const status = axiosError.response?.status;
  const isUnauthorized = status === 401 || status === 403;
  if (!isUnauthorized) {
    throw axiosError;
  }

  // extend the request config with a retry flag
  const originalRequest: RetryConfig = axiosError.config;

  const isRefreshRequest = originalRequest.url?.includes("/token/refresh");
  if (isRefreshRequest || originalRequest._retry) {
    useAuthStore.getState().logout();
    throw axiosError;
  }

  const refreshToken = localStorage.getItem("refresh_token");
  if (!refreshToken) {
    useAuthStore.getState().logout();
    throw axiosError;
  }

  originalRequest._retry = true;

  try {
    const access = await RefreshTokenService.refresh(refreshToken);
    originalRequest.headers["Authorization"] = `Bearer ${access}`;
    return Axios(originalRequest);
  } catch (refreshError: unknown) {
    if (
      Axios.isAxiosError(refreshError) &&
      refreshError.response?.status === 401
    ) {
      useAuthStore.getState().logout();
    }
    throw refreshError instanceof Error
      ? refreshError
      : new Error(
          JSON.stringify(refreshError, null, 2) || "Unknown refresh error",
        );
  }
};

const API_BASE_URL = import.meta.env.BUILD_API_BASE_URL ?? "";

export const intercept = (): void => {
  Axios.defaults.baseURL = `${API_BASE_URL}/core`;
  Axios.defaults.withCredentials = true;
  Axios.interceptors.request.use(onRequest, onRequestError);
  Axios.interceptors.response.use(onResponse, onResponseError);
};
